import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../api";
import "./Admin.css";
import "./AdminEnrollments.css";

const AdminEnrollments = () => {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);

    useEffect(() => {
        const fetchEnrollments = async () => {
            const token = localStorage.getItem("token");
            try {
                const response = await fetch(`${API_BASE}/api/admin/enrollments`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await response.json();
                setEnrollments(data);
            } catch (error) {
                console.error("Error fetching enrollments:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchEnrollments();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/#/admin/login";
    };

    const formatDate = (dateStr) => {
        if (!dateStr || (typeof dateStr === 'object' && Object.keys(dateStr).length === 0)) return "-";
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return "-";
        }
    };

    const safeRender = (val) => {
        if (val === null || val === undefined) return "-";
        if (typeof val === 'object') {
            if (Object.keys(val).length === 0) return "-";
            return JSON.stringify(val);
        }
        return val;
    };

    const stats = {
        total: enrollments.length,
        paid: enrollments.filter(e => String(e.PaymentStatus || "").toLowerCase() === "success").length,
        pending: enrollments.filter(e => String(e.PaymentStatus || "").toLowerCase() !== "success").length
    };

    return (
        <div className="admin-enrollments-wrapper">
            <header className="admin-header-section">
                <div className="admin-title-box">
                    <h1>Enrollments Management</h1>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <Link to="/admin/dashboard" className="lora-admin-btn">Dashboard</Link>
                    <button onClick={handleLogout} className="lora-admin-btn lora-admin-btn-logout">Logout</button>
                </div>
            </header>

            <div className="admin-stats-bar">
                <div className="stat-card">
                    <span className="stat-label">Total Applications</span>
                    <span className="stat-value">{stats.total}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Paid Enrollments</span>
                    <span className="stat-value" style={{ color: 'var(--admin-success)' }}>{stats.paid}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Pending</span>
                    <span className="stat-value" style={{ color: 'var(--admin-pending)' }}>{stats.pending}</span>
                </div>
            </div>

            <nav className="lora-admin-tabs" style={{ marginBottom: '30px' }}>
                <Link to="/admin/dashboard" className="lora-admin-tab">Overview</Link>
                <Link to="/admin/enrollments" className="lora-admin-tab active">Enrollments</Link>
                <Link to="/admin/transactions" className="lora-admin-tab">Transactions</Link>
            </nav>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>Loading enrollments...</div>
            ) : (
                <div className="enrollment-grid">
                    {enrollments.map((en) => (
                        <div 
                            key={en.Id} 
                            className="enrollment-card"
                            onClick={() => setSelectedEnrollment(en)}
                        >
                            <span className={`card-status-badge ${String(en.PaymentStatus || "").toLowerCase() === 'success' ? 'status-paid' : 'status-pending'}`}>
                                {safeRender(en.PaymentStatus)}
                            </span>
                            <div className="card-child-name">{safeRender(en.ChildName)}</div>
                            <span className="card-code">{safeRender(en.EnrollmentCode) || "NO-CODE"}</span>
                            
                            <div className="card-info-row">
                                <span>📅</span> {formatDate(en.DateOfBirth)} ({safeRender(en.Gender)})
                            </div>
                            <div className="card-info-row">
                                <span>🏫</span> {safeRender(en.CurrentSchool)}
                            </div>
                            <div className="card-info-row">
                                <span>⛺</span> {typeof en.CampName === 'string' ? en.CampName.split(':')[0] : safeRender(en.CampName)}
                            </div>

                            <div className="card-footer">
                                <span className="card-amount">₹{Number(en.Amount || 0).toLocaleString()}</span>
                                <span className="view-details-btn">View Details →</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Detailed Popup Modal */}
            {selectedEnrollment && (
                <div className="modal-overlay" onClick={() => setSelectedEnrollment(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>{safeRender(selectedEnrollment.ChildName)}</h2>
                                <p>{safeRender(selectedEnrollment.EnrollmentCode)}</p>
                            </div>
                            <button className="close-modal" onClick={() => setSelectedEnrollment(null)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="details-section">
                                <h3>Basic Information</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Gender</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.Gender)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Date of Birth</span>
                                        <span className="detail-value">{formatDate(selectedEnrollment.DateOfBirth)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Current School</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.CurrentSchool)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Grade/Class</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.GradeClass)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Camp & Payment</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Selected Camp</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.CampName)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Amount Paid</span>
                                        <span className="detail-value">₹{Number(selectedEnrollment.Amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Payment Status</span>
                                        <span className="detail-value" style={{color: 'var(--admin-success)'}}>
                                            {safeRender(selectedEnrollment.PaymentStatus)}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Enrollment Date</span>
                                        <span className="detail-value">{formatDate(selectedEnrollment.CreatedAt)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Parent Contact</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Parent Name</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.ParentName)} ({safeRender(selectedEnrollment.Relationship)})</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Phone</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.ContactNumber)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Email</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.Email)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="details-section">
                                <h3>Emergency & Medical</h3>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <span className="detail-label">Emergency Contact</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.EmergencyContactPerson)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="detail-label">Emergency Phone</span>
                                        <span className="detail-value">{safeRender(selectedEnrollment.EmergencyContactNumber)}</span>
                                    </div>
                                </div>
                                {selectedEnrollment.MedicalConditions && (
                                    <div className="medical-note">
                                        <span className="detail-label">Medical Conditions:</span>
                                        <p style={{margin: '5px 0'}}>{safeRender(selectedEnrollment.MedicalConditions)}</p>
                                    </div>
                                )}
                                {selectedEnrollment.CurrentMedications && (
                                    <div className="medical-note" style={{borderColor: '#0984e3', background: '#e1f5fe'}}>
                                        <span className="detail-label">Current Medications:</span>
                                        <p style={{margin: '5px 0'}}>{safeRender(selectedEnrollment.CurrentMedications)}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEnrollments;
